import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  isDevMode,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";

import { AccountSymbolTable } from "@bitwarden/web-vault/app/components/tables/account-tables/account-symbol-table/account-symbol-table";
import {
  actionButtonOptions,
  institutionInputOptions,
} from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/accounts-add-edit/component.options";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { FormGroup } from "@angular/forms";
import {
  isOfType,
  Origin,
  SyncStatusEnum,
} from "@bitwarden/web-vault/app/models/types/general-types";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { ViewsService } from "@bitwarden/web-vault/app/services/views/views.service";
import { OnBackDirective } from "@bitwarden/web-vault/app/directives/onBack.directive";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { VaultFileDisplay } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/account-details/vault-file-display";
import {
  AllowedCurrencies,
  CurrencyType,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { SyncStatusView } from "@bitwarden/web-vault/app/models/view/sync-status/sync-status.view";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { GlossSync } from "@bitwarden/web-vault/app/services/syncing/gloss.sync";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { DeleteItem, deletingItem } from "@bitwarden/web-vault/app/components/tables/deleteItem";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { SyncTag, SyncTagConstant } from "@bitwarden/web-vault/app/models/types/sync.types";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { BasiqUserAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.user";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { catchError, defer, forkJoin, from, map, of, switchMap } from "rxjs";
import { BasiqUserValidator } from "@bitwarden/web-vault/app/services/syncing/basiq/validators/basiq.validator.user";
import { Router } from "@angular/router";
import { basiqIntegrationEnabled } from "@bitwarden/web-vault/utils/flags";

import { WizardType } from "@bitwarden/web-vault/app/services/store/user/wizards.store.collection";
import { renameDuplicateName } from "@bitwarden/web-vault/app/shared/utils/helper-new-view-name";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { FiskilApi } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.api";
import { hasActiveFiskilConsentCoveringAccount } from "./has-active-fiskil-consent-covering-account";
import {
  hasActivePlaidItemCoveringAccount,
  plaidItemWithNewAccounts,
  PlaidItemContext,
  requiresPlaidReauth,
} from "./has-active-plaid-item-covering-account";
import { PlaidApi } from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.api";
import { canFetchSyncedAccountData } from "./can-fetch-synced-account-data";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { DeviceDetectorService } from "ngx-device-detector";
import { getPluginIdForAccount } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-account";
import { AUSTRALIAN_SUPER_PLUGIN_NAME } from "@bitwarden/web-vault/app/services/store/data-plugin/australian-super.plugin-config";
import { getPluginMetadata } from "@bitwarden/web-vault/app/models/data/plugin-metadata";
import { setAccountInstitutionLink } from "@bitwarden/web-vault/app/shared/utils/helper.account/institution-link.helper";

const SYNC_TAG: SyncTagConstant = {
  syncing: "syncing",
  synced: "successful",
  error: "error",
};

@Component({
  selector: "app-account-details",
  templateUrl: "./account-details.component.html",
})
export class AccountDetailsComponent extends OnBackDirective implements OnChanges, AfterViewInit {
  @Input() accountId: string;
  @Input() transactionCount: number = 0;
  /** Emits the collection ids picked in the share-account slide-out, with the id of the account
   * the slide-out was opened for (not whatever row is selected at confirm time); the parent
   * (ManageAccountsComponent) owns the actual fork/share network calls. */
  @Output() shareToCollections = new EventEmitter<{ accountId: string; collectionIds: string[] }>();

  /** Static footer template for the share-account slide-out (see openShareAccountDialog) — only
   * this component's own template can provide one that DialogWrapperComponent will pin to the
   * bottom of the panel; ShareAccountComponent hands over the live button via `shareButtonOptions`. */
  @ViewChild("shareFooter", { static: false }) private shareFooterTemplate!: TemplateRef<unknown>;
  protected shareButtonOptions?: ActionButton;

  /** Synced from `accountId` input so origin-specific computeds re-run when the details pane changes account. */
  private readonly accountIdSignal = signal<string | undefined>(undefined);

  /** Vault account for the details pane (reactive via `accountIdSignal`). */
  private readonly selectedAccount = computed(() => {
    const accountId = this.accountIdSignal();
    if (!accountId) {
      return undefined;
    }
    return this.userStore.enrichAccounts.collection()?.find((a) => a.id === accountId);
  });

  private i18nService: I18nService = inject(I18nService);
  private viewsService: ViewsService = inject(ViewsService);
  private basiqUserAction = inject(BasiqUserAction);
  private basiqUserValidator = inject(BasiqUserValidator);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private userStore: UserStoreService = inject(UserStoreService);
  private importStore: ImportStoreService = inject(ImportStoreService);
  private appState = inject(AppStateStoreService);
  private glossSync = inject(GlossSync);
  private calculatedPastStore: CalculationPastStoreService = inject(CalculationPastStoreService);
  private elementRef: ElementRef = inject(ElementRef);
  private router: Router = inject(Router);
  private fiskilApi = inject(FiskilApi);
  private plaidApi = inject(PlaidApi);

  protected readonly deviceDetectorService = inject(DeviceDetectorService);
  protected readonly Origin = Origin;
  protected vaultFileDisplay: VaultFileDisplay;
  protected dialogFactory: DialogFactory;
  protected deleteItem: DeleteItem;
  protected institutionInputOptions = institutionInputOptions;
  protected readonly isDevMode = isDevMode;
  protected readonly SYNC_TAG = SYNC_TAG;

  successfulStatus: SyncStatusView;
  statusTag: SyncTag;
  accountNamePlaceHolder: string = "";
  syncStates: SyncStatusView[];
  form: FormGroup;
  account: AccountView;
  accountName: string;
  accountNickname: string;
  selectedInstitution: InstitutionView;
  selectedAccountProduct: InstitutionAccountView;
  selectedCurrency: CurrencyType;
  saveButtonOptions = new ActionButton({
    ...actionButtonOptions,
    text: this.i18nService.t("save"),
    onClick: this.submit.bind(this),
  });
  /** A save is in flight. AccountFormComponent renders a copy of saveButtonOptions, so the
   *  submit-type self-disable (Button.onButtonClicked) never reaches the visible Save button. */
  private isSaving = false;
  table: AccountSymbolTable = new TablesFactory().get(TablesType.AccountSymbol);
  cancelButtonOptions = new ActionButton({
    class: "neutral",
    text: this.i18nService.t("cancel"),
    onClick: this.handleBackClick.bind(this),
  });

  private readonly isAuthSignal = toSignal(
    defer(() =>
      basiqIntegrationEnabled()
        ? from(this.basiqUserValidator.isBasiqUser()).pipe(
            switchMap((isUser) => {
              if (!isUser) {
                return of(false);
              }
              return from(this.basiqUserAction.getActiveConsent()).pipe(
                map((consent) => !!consent),
              );
            }),
          )
        : of(false),
    ),
    { initialValue: undefined },
  );

  private readonly hasPremiumWithAccounts = computed(() => {
    const basiqAccounts = this.userStore.getBasiqAccounts();
    const isPremium = this.userStore.subscription.activeSubscription()?.isPremium;
    return isPremium && basiqAccounts.length > 0;
  });

  private readonly isAuthResolved = computed(() => this.isAuthSignal() !== undefined);

  readonly canConnectToInstitution = computed(
    () => this.isAuthResolved() && !!this.isAuthSignal() && this.hasPremiumWithAccounts(),
  );

  /**
   * Active Fiskil consents + banking accounts (for arrangement_id) loaded once when this view is created.
   */
  private readonly fiskilConsentAndAccounts = toSignal(
    defer(() =>
      forkJoin({
        consents: from(this.fiskilApi.getActiveConsents()).pipe(map((r) => r.consents ?? [])),
        accounts: from(this.fiskilApi.getAccounts()).pipe(map((r) => r.accounts ?? [])),
      }),
    ),
    { initialValue: undefined },
  );

  private readonly plaidRefreshTrigger = signal(0);

  private readonly plaidItemCtx = toSignal(
    toObservable(this.plaidRefreshTrigger).pipe(
      switchMap(() =>
        this.selectedAccount()?.origin !== Origin.plaid
          ? of({ items: [] } as PlaidItemContext)
          : from(this.plaidApi.getItems()).pipe(
              map((r) => ({ items: r.items ?? [] }) as PlaidItemContext),
              catchError(() => of({ items: [] } as PlaidItemContext)),
            ),
      ),
    ),
    { initialValue: undefined as PlaidItemContext | undefined },
  );

  private refreshPlaidCtx(): void {
    this.plaidRefreshTrigger.update((n) => n + 1);
  }

  /**
   * Premium user with at least one active Fiskil consent whose arrangement matches this account (CDR).
   * Non-Fiskil origins are always false.
   */
  readonly hasActiveFiskilConsentCoveringAccount = computed(() =>
    hasActiveFiskilConsentCoveringAccount(
      !!this.userStore.subscription.activeSubscription()?.isPremium,
      this.fiskilConsentAndAccounts(),
      this.accountIdSignal(),
      this.userStore.enrichAccounts.collection(),
    ),
  );

  readonly hasActivePlaidItemCoveringAccount = computed(() =>
    hasActivePlaidItemCoveringAccount(
      !!this.userStore.subscription.activeSubscription()?.isPremium,
      this.plaidItemCtx(),
      this.accountIdSignal(),
      this.userStore.enrichAccounts.collection(),
    ),
  );

  readonly requiresPlaidReauth = computed(() =>
    requiresPlaidReauth(
      !!this.userStore.subscription.activeSubscription()?.isPremium,
      this.plaidItemCtx(),
      this.accountIdSignal(),
      this.userStore.enrichAccounts.collection(),
    ),
  );

  readonly plaidItemWithNewAccounts = computed(() =>
    plaidItemWithNewAccounts(
      this.plaidItemCtx(),
      this.selectedAccount()?.c_institution?.instoPlaidId,
    ),
  );

  /** Org-owned (shared) accounts have no sync path yet, so Fetch data is disabled for them —
   *  see isExcludedFromAutoSync. */
  readonly isOrganizationOwned = computed(() => !!this.selectedAccount()?.isOrganizationOwned?.());

  /** Origin-specific Fetch Data enablement — Plaid must not use Fiskil consent. */
  readonly canFetchSyncedData = computed(() =>
    canFetchSyncedAccountData(this.selectedAccount()?.origin, {
      fiskilConsent: this.hasActiveFiskilConsentCoveringAccount(),
      plaidItem: this.hasActivePlaidItemCoveringAccount(),
      basiqHasActiveConsent: this.isAuthResolved() && !!this.isAuthSignal(),
    }),
  );

  readonly requiresReauth = computed(() => {
    const account = this.selectedAccount();
    return (
      account?.origin === Origin.basiq &&
      this.isAuthResolved() &&
      !this.isAuthSignal() &&
      this.hasPremiumWithAccounts()
    );
  });

  protected get isSystemAccount(): boolean {
    return this.account?.origin === Origin.system;
  }

  /** Mirrors AccountFormComponent.isFormDisabled: system accounts and accounts with a successful
   *  sync (fiskil, plaid, basiq, plugins like Opal Card) have their details locked, so the header
   *  edit icon, which only scrolls to that form, is hidden for them. */
  protected get isAccountDetailsLocked(): boolean {
    return this.isSystemAccount || !!this.successfulStatus;
  }

  /** Plugin id (e.g. "opal-card", "super") for the selected account, if it's a plugin account. */
  readonly pluginId = computed(() => getPluginIdForAccount(this.selectedAccount()));

  readonly pluginMetadata = computed(() => {
    const id = this.pluginId();
    return id ? getPluginMetadata(id) : undefined;
  });

  /** Exposed for the Australian Super OTP hint in the template. */
  protected readonly AUSTRALIAN_SUPER_PLUGIN_NAME = AUSTRALIAN_SUPER_PLUGIN_NAME;

  /** Plugin accounts have no institution to reconnect to — send the user to the Plugins page instead. */
  goToPlugin(): void {
    void this.router.navigate(["/", GlossRoutes.Plugins]);
  }

  /** Reconnects/fetches via the account's plugin (syncOnly: reuses saved credentials), or through
   *  the normal basiq/plaid/fiskil sync path for a non-plugin account. */
  async onFetchDataClick(): Promise<void> {
    const pluginId = this.pluginId();
    if (pluginId) {
      // origin: Back closes the dialog, back to this account, not into the add-account flow (#5451).
      this.dialogFactory.openPluginConfigDialog(pluginId, {
        syncOnly: true,
        origin: "account-details",
        // Sync just this account, not every account under the plugin login (#5450).
        accountId: this.selectedAccount()?.id,
      });
      return;
    }
    await this.startSync();
  }

  constructor() {
    super();
    this.vaultFileDisplay = new VaultFileDisplay();
    this.dialogFactory = new DialogFactory();
    this.deleteItem = new DeleteItem(deletingItem.account);

    effect(() => {
      this.syncStates = this.importStore.syncStatus.syncStatusViews();
      if (this.syncStates) {
        this.account.c_syncStatus = this.syncStates.filter((s) => s.accountId === this.accountId);
        this.setSuccessfulSync();
        this.setLastSync();
      }

      this.vaultFileDisplay.vaultFileViews = this.userStore.vaultFiles.vaultFileViews();
      this.vaultFileDisplay.listFilteredFiles(this.accountId);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.accountIdSignal.set(this.accountId);
    if (changes["accountId"]) {
      this.refreshPlaidCtx();
    }
    this.syncStates = this.importStore.syncStatus.syncStatusViews();
    this.account = this.userStore.enrichAccounts
      .collection()
      .find((account) => account.id === this.accountId)
      .clone();
    // Show the original product/institution name in the form (not the user-set nickname).
    this.accountName = this.account.originalName;
    this.accountNickname = this.account.origin !== Origin.manual ? this.account.nickname : null;
    this.selectedCurrency = this.account.currency;
    this.selectedInstitution = this.account.c_institution;
    this.selectedAccountProduct = this.account.c_institutionAccountType;
    this.account.c_syncStatus = this.syncStates.filter((s) => s.accountId === this.accountId);
    this.setSuccessfulSync();
    this.setLastSync();
    this.institutionInputOptions = {
      ...this.institutionInputOptions,
      value: this.selectedInstitution?.name,
    };

    this.updateVaultFileDisplay();
    // table is a class property (not a @ViewChild), so this is safe before ngAfterViewInit.
    this.table.setAccountId(this.accountId);
  }

  ngAfterViewInit() {
    this.updateVaultFileDisplay();
  }

  private setSuccessfulSync() {
    const filteredAndSorted = this.account.c_syncStatus
      .filter((item) => item.key === "synced")
      .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
    this.successfulStatus = filteredAndSorted[0];
  }

  private setLastSync() {
    const filteredAndSorted: SyncStatusView[] = this.account.c_syncStatus.sort(
      (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
    );
    switch (filteredAndSorted[0]?.key) {
      case undefined:
        this.statusTag = undefined; // never fetched - not an error
        return;
      case SyncStatusEnum.syncing:
        this.statusTag = SYNC_TAG.syncing;
        return;
      case SyncStatusEnum.synced:
        this.statusTag = SYNC_TAG.synced;
        break;
      default:
        this.statusTag = SYNC_TAG.error;
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: any) {
    this.updateVaultFileDisplay();
  }

  private updateVaultFileDisplay() {
    const size = this.containerSize();
    // Skip when container is not yet in the DOM (ngOnChanges fires before ngAfterViewInit on first
    // render) or when it has zero width (e.g. panel is hidden). A resize event to 0 will not
    // refresh the list — if the panel can transition from hidden to visible, call this explicitly.
    if (size > 0) {
      this.vaultFileDisplay.vaultFileContainerWidth = size;
      this.vaultFileDisplay.listFilteredFiles(this.accountId);
    }
  }

  private containerSize() {
    const fileContainerElement = this.elementRef.nativeElement.querySelector("#fileContainer");
    return fileContainerElement?.offsetWidth ?? 0;
  }

  moreVaultFile() {
    // eslint-disable-next-line no-console
    console.log("account-details.component.ts line121 moreVaultFile button clicked");
  }

  // The header's edit icon has no separate edit mode to enter — the account form below is
  // always rendered in edit mode ([isEditMode]="true") — so wire it to scroll that section
  // into view instead of leaving the click with no effect.
  scrollToAccountDetail() {
    this.elementRef.nativeElement
      .querySelector("#account-detail-section")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  setAccountName(name: string) {
    this.accountName = name;
  }

  setAccountNickname(name: string) {
    this.accountNickname = name;
  }

  clearAccountName() {
    this.accountName = null;
  }

  selectCurrency(currency: string) {
    this.selectedCurrency = currency as CurrencyType;
  }

  // todo: same code in account-add-edit
  onSelectAccountProduct(selected: InstitutionAccountView) {
    this.selectedAccountProduct = selected;
    this.accountNamePlaceHolder = `My ${selected.name}`;
  }

  goToPastView(): void {
    this.calculatedPastStore.filters.filterOnAccount(this.account);
    this.viewsService.view = View.PAST;
  }

  async startSync() {
    await this.glossSync.sync([this.account]);
  }

  /** Opens manage institutions with this account's institution selected and details visible. */
  goToInstitutionDetail(): void {
    if (!this.selectedInstitution?.id) {
      return;
    }
    void this.router.navigate(["/manage", GlossRoutes.Institutions], {
      queryParams: { institutionId: this.selectedInstitution.id },
    });
  }

  /** Reconnects the account's institution via origin-specific re-auth (Basiq redirect, Plaid Update Mode). */
  async reauthorize() {
    this.appState.startProcess(
      "syncing-data",
      `Connecting to ${this.selectedInstitution.name} for reauthorization`,
    );
    await this.glossSync.reconnectToInstitution(this.account.origin, this.selectedInstitution);
    this.refreshPlaidCtx();
    this.appState.endProcess("syncing-data");
    return;
  }

  async addPlaidAccounts(): Promise<void> {
    this.appState.startProcess(
      "syncing-data",
      `Adding accounts from ${this.selectedInstitution?.name ?? "Plaid"}`,
    );
    await this.glossSync.addPlaidAccounts(this.selectedInstitution);
    this.refreshPlaidCtx();
    this.appState.endProcess("syncing-data");
  }

  addBalance() {
    this.dialogFactory.addBalance(this.account);
  }

  addTransaction() {
    this.dialogFactory.addTransaction(this.account);
  }

  openShareAccountDialog() {
    const accountId = this.account.id;
    this.dialogFactory.shareAccount(
      this.account,
      (collectionIds) => this.shareToCollections.emit({ accountId, collectionIds }),
      (options) => (this.shareButtonOptions = options),
      this.shareFooterTemplate,
    );
  }

  deleteAccount() {
    this.deleteItem.delete(this.account).then((response) => {
      if (response && this.userStore.accounts.accountViews().length === 0) {
        const accountWizard = this.userStore.wizards.findWizardByType(WizardType.account);
        if (accountWizard) {
          this.userStore.wizards
            .delete(accountWizard)
            .then(() => this.router.navigate(["/"]).then());
        }
      }
    });
    this.handleBackClick();
  }

  private async submit() {
    if (this.isSaving) {
      return;
    }
    const isNameChanged = this.accountName !== this.account.originalName;
    this.isSaving = true;
    try {
      if (!this.account || !this.accountName) {
        return;
      }

      const updateAccount = this.account.clone();
      if (isNameChanged) {
        this.accountName = renameDuplicateName(
          this.userStore.accounts.accountViews(),
          this.accountName,
        );
        updateAccount.name = this.accountName;
      }
      setAccountInstitutionLink(updateAccount, {
        institutionId: this.selectedInstitution.id,
        institutionAccountId: this.selectedAccountProduct ? this.selectedAccountProduct.id : null,
      });

      updateAccount.c_institutionAccountType = this.selectedAccountProduct;

      if (isOfType<CurrencyType>(this.selectedCurrency, AllowedCurrencies)) {
        updateAccount.currency = this.selectedCurrency;
      } else {
        this.notifyService.notify(NotificationEnum.error, "errorOccurred", "InvalidCurrency");
        return;
      }

      // Apply nickname only for synced/fetched accounts (not for manual accounts)
      if (updateAccount.origin !== Origin.manual) {
        updateAccount.nickname = this.accountNickname;
      }

      await this.userStore.accounts.save(updateAccount);
      this.notifyService.notify(NotificationEnum.success, "success", "updatedSuccessfully");
      if (isNameChanged) {
        this.account.name = updateAccount.name;
      }
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "somethingWentWrong");
    } finally {
      this.isSaving = false;
      this.saveButtonOptions = new ActionButton({
        ...actionButtonOptions,
        isEnabled: true,
        text: this.i18nService.t("save"),
        onClick: this.submit.bind(this),
      });
    }
  }
}
